import { useEffect, useState, useRef } from 'react';
import {
  X,
  Gamepad2,
  Search,
  Loader2,
  Check,
  Calendar,
} from 'lucide-react';
import { gamesAPI } from '../api/games';
import { getCountryFlag } from '../utils/formatters';

/**
 * Right-side slide-in panel with every advanced creator filter.
 * Filters apply live (parent is expected to debounce its fetch).
 *
 * Props:
 *   isOpen: boolean
 *   onClose: () => void
 *   filters: the page-level filter state object
 *   setFilters: setter for that state
 *   allCountries, allGenres, allTiers: lookup arrays from the page
 */
const SIZE_BUCKETS = [
  { value: 'Elite',   label: 'Elite (1M+)',        color: 'bg-emerald-400' },
  { value: 'Top',     label: 'Top (500K – 1M)',    color: 'bg-sc-pink' },
  { value: 'Rising',  label: 'Rising (100K – 500K)', color: 'bg-purple-400' },
  { value: 'Growing', label: 'Growing (10K – 100K)', color: 'bg-amber-400' },
  { value: 'Starter', label: 'Starter (< 10K)',    color: 'bg-gray-400' },
];

const PLATFORMS = [
  { value: 'youtube', label: 'YouTube' },
  { value: 'twitch', label: 'Twitch' },
  { value: 'kick', label: 'Kick' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'instagram', label: 'Instagram' },
];

// Convert a filter value that may be a comma-string or array into an array.
const toArray = (v) => (Array.isArray(v) ? v : v ? String(v).split(',').filter(Boolean) : []);
const toCsv = (arr) => (arr.length ? arr.join(',') : '');

export default function CreatorFiltersModal({
  isOpen,
  onClose,
  filters,
  setFilters,
  allCountries = [],
  allGenres = [],
}) {
  const [gameQuery, setGameQuery] = useState('');
  const [gameResults, setGameResults] = useState([]);
  const [gameSearching, setGameSearching] = useState(false);
  const [countryQuery, setCountryQuery] = useState('');
  const [genreQuery, setGenreQuery] = useState('');
  const panelRef = useRef(null);

  // Debounced game autocomplete
  useEffect(() => {
    if (!gameQuery || gameQuery.length < 2) {
      setGameResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        setGameSearching(true);
        const response = await gamesAPI.getAll({ search: gameQuery, limit: 8 });
        setGameResults(response.data.games || []);
      } catch (err) {
        console.error('Game search failed:', err);
        setGameResults([]);
      } finally {
        setGameSearching(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [gameQuery]);

  // Close on ESC
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const sizesArr = toArray(filters.sizes);
  const platformsArr = toArray(filters.platform);
  const countriesArr = toArray(filters.country);
  const genresArr = toArray(filters.genre);

  const toggle = (key, value) => {
    const current = toArray(filters[key]);
    const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
    setFilters({ ...filters, [key]: toCsv(next) });
  };

  const selectGame = (g) => {
    setFilters({ ...filters, gameId: g.id, gameName: g.title });
    setGameQuery('');
    setGameResults([]);
  };

  const clearGame = () => {
    setFilters({ ...filters, gameId: '', gameName: '', gameFrom: '', gameTo: '' });
  };

  const countriesFiltered = countryQuery
    ? allCountries.filter((c) => c.toLowerCase().includes(countryQuery.toLowerCase()))
    : allCountries;

  const genresFiltered = genreQuery
    ? allGenres.filter((g) => g.toLowerCase().includes(genreQuery.toLowerCase()))
    : allGenres;

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div
        onClick={onClose}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
      />

      {/* Slide-in panel */}
      <div
        ref={panelRef}
        className="relative ml-auto h-full w-full max-w-md bg-gray-950 border-l border-white/10 shadow-2xl overflow-y-auto"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-gray-950/95 backdrop-blur-sm border-b border-white/10 px-5 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Filters</h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-white/10 text-white hover:text-white transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-7">
          {/* Game + coverage window */}
          <Section icon={<Gamepad2 className="w-4 h-4" />} title="Game coverage">
            {filters.gameId ? (
              <div className="flex items-center gap-2 pl-3 pr-2 py-2 bg-sc-pink/10 border border-sc-pink/25 rounded-lg">
                <Gamepad2 className="w-4 h-4 text-sc-pink" />
                <span className="flex-1 truncate text-sm text-white font-medium">{filters.gameName}</span>
                <button onClick={clearGame} className="text-white hover:text-white p-0.5">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white" />
                <input
                  type="text"
                  placeholder="Search for a game…"
                  value={gameQuery}
                  onChange={(e) => setGameQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 bg-white/5 border border-white rounded-lg text-sm text-white placeholder-white focus:outline-none focus:border-sc-pink/40"
                />
                {gameQuery.length >= 2 && (
                  <div className="mt-1 bg-gray-900 border border-white rounded-lg max-h-60 overflow-y-auto">
                    {gameSearching ? (
                      <div className="px-3 py-2 text-xs text-white flex items-center gap-2">
                        <Loader2 className="w-3 h-3 animate-spin" /> Searching…
                      </div>
                    ) : gameResults.length === 0 ? (
                      <div className="px-3 py-2 text-xs text-white">No games found</div>
                    ) : (
                      gameResults.map((g) => (
                        <button
                          key={g.id}
                          onClick={() => selectGame(g)}
                          className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-white hover:bg-white/10"
                        >
                          {g.coverImageUrl ? (
                            <img src={g.coverImageUrl} alt="" className="w-6 h-6 rounded object-cover" />
                          ) : (
                            <div className="w-6 h-6 rounded bg-white/10 flex items-center justify-center">
                              <Gamepad2 className="w-3.5 h-3.5 text-white" />
                            </div>
                          )}
                          <span className="flex-1 truncate">{g.title}</span>
                          {g._count?.Content > 0 && (
                            <span className="text-[10px] text-white">{g._count.Content}</span>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Date window */}
            {filters.gameId && (
              <div className="mt-3">
                <label className="text-[11px] text-white uppercase tracking-wide">Coverage window</label>
                <div className="mt-1.5 flex gap-2 flex-wrap">
                  {['30', '60', '90', '180', '365'].map((d) => (
                    <button
                      key={d}
                      onClick={() => setFilters({ ...filters, gameDays: d, gameFrom: '', gameTo: '' })}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                        filters.gameDays === d && !filters.gameFrom && !filters.gameTo
                          ? 'bg-sc-pink/20 border border-sc-pink/30 text-sc-pink'
                          : 'bg-white/5 border border-white text-white hover:bg-white/10'
                      }`}
                    >
                      {d}d
                    </button>
                  ))}
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] text-white uppercase tracking-wide">From</label>
                    <div className="relative mt-1">
                      <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white" />
                      <input
                        type="date"
                        value={filters.gameFrom || ''}
                        onChange={(e) => setFilters({ ...filters, gameFrom: e.target.value, gameDays: '' })}
                        className="w-full pl-8 pr-2 py-1.5 bg-white/5 border border-white rounded-lg text-xs text-white focus:outline-none focus:border-sc-pink/40"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] text-white uppercase tracking-wide">To</label>
                    <div className="relative mt-1">
                      <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white" />
                      <input
                        type="date"
                        value={filters.gameTo || ''}
                        onChange={(e) => setFilters({ ...filters, gameTo: e.target.value, gameDays: '' })}
                        className="w-full pl-8 pr-2 py-1.5 bg-white/5 border border-white rounded-lg text-xs text-white focus:outline-none focus:border-sc-pink/40"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Section>

          {/* Platforms */}
          <Section title="Platforms">
            <div className="grid grid-cols-2 gap-1.5">
              {PLATFORMS.map((p) => (
                <CheckboxRow
                  key={p.value}
                  checked={platformsArr.includes(p.value)}
                  label={p.label}
                  onClick={() => toggle('platform', p.value)}
                />
              ))}
            </div>
          </Section>

          {/* Sizes */}
          <Section title="Sizes">
            <div className="space-y-1">
              {SIZE_BUCKETS.map((s) => (
                <CheckboxRow
                  key={s.value}
                  checked={sizesArr.includes(s.value)}
                  label={<span className="flex items-center gap-2"><span className={`w-2 h-2 rounded-full ${s.color}`} />{s.label}</span>}
                  onClick={() => toggle('sizes', s.value)}
                />
              ))}
            </div>
          </Section>

          {/* Regions / Countries */}
          <Section title={`Regions ${countriesArr.length ? `(${countriesArr.length})` : ''}`}>
            <div className="relative mb-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white" />
              <input
                type="text"
                placeholder="Find country…"
                value={countryQuery}
                onChange={(e) => setCountryQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-1.5 bg-white/5 border border-white rounded-lg text-xs text-white placeholder-white focus:outline-none focus:border-sc-pink/40"
              />
            </div>
            <div className="max-h-44 overflow-y-auto space-y-0.5 pr-1">
              {countriesFiltered.map((c) => (
                <CheckboxRow
                  key={c}
                  checked={countriesArr.includes(c)}
                  label={<>{getCountryFlag(c)} {c}</>}
                  onClick={() => toggle('country', c)}
                />
              ))}
              {countriesFiltered.length === 0 && (
                <p className="text-xs text-white px-2 py-1">No countries match</p>
              )}
            </div>
          </Section>

          {/* Genres */}
          <Section title={`Genres ${genresArr.length ? `(${genresArr.length})` : ''}`}>
            <div className="relative mb-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white" />
              <input
                type="text"
                placeholder="Find genre…"
                value={genreQuery}
                onChange={(e) => setGenreQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-1.5 bg-white/5 border border-white rounded-lg text-xs text-white placeholder-white focus:outline-none focus:border-sc-pink/40"
              />
            </div>
            <div className="max-h-44 overflow-y-auto space-y-0.5 pr-1">
              {genresFiltered.map((g) => (
                <CheckboxRow
                  key={g}
                  checked={genresArr.includes(g)}
                  label={g}
                  onClick={() => toggle('genre', g)}
                />
              ))}
              {genresFiltered.length === 0 && (
                <p className="text-xs text-white px-2 py-1">No genres match</p>
              )}
            </div>
          </Section>

          {/* Keywords */}
          <Section title="Keywords / tags">
            <input
              type="text"
              placeholder="e.g. valorant, vtuber, family-friendly"
              value={filters.keywords || ''}
              onChange={(e) => setFilters({ ...filters, keywords: e.target.value })}
              className="w-full px-3 py-2 bg-white/5 border border-white rounded-lg text-sm text-white placeholder-white focus:outline-none focus:border-sc-pink/40"
            />
            <p className="mt-1.5 text-[11px] text-white">
              Comma or space separated. Matches creator name, handle, niche, and genres.
            </p>
          </Section>

          {/* Contact email */}
          <Section title="Contact email">
            <div className="grid grid-cols-3 gap-1.5">
              {[
                { value: '', label: 'Any' },
                { value: 'true', label: 'Has email' },
                { value: 'false', label: 'No email' },
              ].map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setFilters({ ...filters, hasEmail: opt.value })}
                  className={`px-3 py-2 rounded-lg text-xs font-medium transition ${
                    (filters.hasEmail || '') === opt.value
                      ? 'bg-sc-pink/20 border border-sc-pink/30 text-sc-pink'
                      : 'bg-white/5 border border-white text-white hover:bg-white/10'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}

// Small presentational helpers -----------------------------------------------

function Section({ title, icon, children }) {
  return (
    <div>
      <h3 className="flex items-center gap-1.5 text-xs font-semibold text-white uppercase tracking-wide mb-2">
        {icon}
        {title}
      </h3>
      {children}
    </div>
  );
}

function CheckboxRow({ checked, label, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-sm text-left transition ${
        checked
          ? 'bg-sc-pink/15 text-white'
          : 'text-white hover:bg-white/5 hover:text-white'
      }`}
    >
      <span
        className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
          checked ? 'bg-sc-pink border-sc-pink' : 'border-white'
        }`}
      >
        {checked && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
      </span>
      <span className="flex-1 truncate">{label}</span>
    </button>
  );
}
